import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Trash2,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTable } from '@/components/DataTable'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Modal } from '@/components/Modal'
import { useDeleteAuditLogs, useTeamAuditLogs } from '@/features/team/queries'
import type { AuditLogRow } from '@/features/team/types'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'

const PAGE_SIZE = 50

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleString() : '-')

const actionLabel = (a: string) => a.replace(/_/g, ' ').replace(/\./g, ' › ')

// Renders three-line actor cell: email (primary), name (secondary), role
// (tertiary badge). Falls back to "User {id}" or "System" when fields
// are missing - matches what audit-service.js returns for legacy rows
// or system-actor entries.
function ActorCell({ row }: { row: AuditLogRow }) {
  const primary = row.actor_email || row.actor_name || (row.actor_id ? `User ${row.actor_id}` : 'System')
  const showName = row.actor_name && row.actor_email
  const role = row.actor_role_name
  return (
    <div className="flex flex-col gap-0.5 leading-tight">
      <span className="text-xs">{primary}</span>
      {showName ? (
        <span className="text-[11px] text-muted-foreground">{row.actor_name}</span>
      ) : null}
      {role ? (
        <Badge variant="secondary" className="w-fit text-[10px]">
          {role}
        </Badge>
      ) : null}
    </div>
  )
}

export function AuditTab() {
  const { toast } = useToast()
  const { hasPermission } = useAuth()
  const canDelete = hasPermission('audit.delete')

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmMode, setConfirmMode] = useState<'selected' | 'all' | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      setPage(0)
    }, 250)
    return () => clearTimeout(t)
  }, [searchInput])

  const auditQ = useTeamAuditLogs({
    search: debouncedSearch,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })
  const deleteM = useDeleteAuditLogs()

  const logs = useMemo(() => auditQ.data?.logs ?? [], [auditQ.data?.logs])
  const total = auditQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE)

  const allIdsOnPage = useMemo(
    () =>
      logs
        .map((r) => (r.id != null ? String(r.id) : null))
        .filter((v): v is string => v !== null),
    [logs],
  )

  const allSelected =
    allIdsOnPage.length > 0 && allIdsOnPage.every((id) => selectedIds.has(id))

  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        for (const id of allIdsOnPage) next.delete(id)
      } else {
        for (const id of allIdsOnPage) next.add(id)
      }
      return next
    })
  }, [allSelected, allIdsOnPage])

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const runDelete = async () => {
    if (!confirmMode) return
    try {
      const body =
        confirmMode === 'all'
          ? { all: true }
          : { ids: Array.from(selectedIds) }
      const r = await deleteM.mutateAsync(body)
      toast({
        title: `${r.deleted} audit ${r.deleted === 1 ? 'entry' : 'entries'} deleted.`,
      })
      setSelectedIds(new Set())
      setConfirmMode(null)
      setPage(0)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Could not delete audit logs',
        description: e instanceof Error ? e.message : 'Please try again.',
      })
    }
  }

  const columns: ColumnDef<AuditLogRow>[] = useMemo(() => {
    const base: ColumnDef<AuditLogRow>[] = []
    if (canDelete) {
      base.push({
        id: 'select',
        header: () => (
          <Checkbox
            checked={allSelected}
            onCheckedChange={() => toggleAllOnPage()}
            aria-label="Select all on this page"
          />
        ),
        cell: ({ row }) => {
          const id = row.original.id != null ? String(row.original.id) : null
          if (!id) return null
          return (
            <Checkbox
              checked={selectedIds.has(id)}
              onCheckedChange={() => toggleOne(id)}
              aria-label={`Select audit row ${id}`}
            />
          )
        },
      })
    }
    base.push(
      {
        accessorKey: 'created_at',
        header: 'When',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {fmtDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'actor',
        header: 'Actor',
        cell: ({ row }) => <ActorCell row={row.original} />,
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) => (
          <Badge variant="outline" className="font-mono text-[10px]">
            {actionLabel(row.original.action)}
          </Badge>
        ),
      },
    )
    return base
  }, [allSelected, canDelete, selectedIds, toggleAllOnPage, toggleOne])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[260px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            placeholder="Search by action, actor, resource…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            className="pl-9"
          />
        </div>
        {canDelete ? (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedIds.size === 0 || deleteM.isPending}
              onClick={() => setConfirmMode('selected')}
            >
              <Trash2 className="size-4" />
              Delete selected ({selectedIds.size})
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={total === 0 || deleteM.isPending}
              onClick={() => setConfirmMode('all')}
            >
              <Trash2 className="size-4 text-destructive" />
              Clear all
            </Button>
          </>
        ) : null}
      </div>

      <Card>
        <CardContent className="p-0">
          {auditQ.isLoading ? (
            <div className="py-10">
              <LoadingSpinner />
            </div>
          ) : auditQ.isError ? (
            <Alert variant="destructive" className="m-3">
              <AlertDescription>Failed to load audit logs.</AlertDescription>
            </Alert>
          ) : (
            // Fixed-height scroll container - after ~20 rows the list
            // scrolls inside the card instead of pushing the page.
            <div className="max-h-[28rem] overflow-auto">
              <DataTable
                columns={columns}
                rows={logs}
                empty={debouncedSearch ? 'No audit entries match your search.' : 'No audit activity yet.'}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {total === 0
            ? 'No entries.'
            : `Showing ${rangeStart}–${rangeEnd} of ${total} ${total === 1 ? 'entry' : 'entries'}.`}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={page === 0 || auditQ.isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="size-4" /> Previous
          </Button>
          <span className="px-2">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={page + 1 >= totalPages || auditQ.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      <Modal.Confirm
        open={confirmMode !== null}
        onOpenChange={(o) => !o && setConfirmMode(null)}
        title={confirmMode === 'all' ? 'Clear all audit logs?' : 'Delete selected entries?'}
        body={
          confirmMode === 'all' ? (
            <>
              <p>
                This permanently removes <strong>every</strong> audit log entry for your workspace.
              </p>
              <p className="mt-2 text-destructive">
                This cannot be undone. A new audit entry will be recorded noting that you cleared the history.
              </p>
            </>
          ) : (
            <p>
              Permanently delete {selectedIds.size} selected{' '}
              {selectedIds.size === 1 ? 'entry' : 'entries'}? This cannot be undone.
            </p>
          )
        }
        confirmLabel={deleteM.isPending ? 'Deleting…' : 'Delete'}
        confirmVariant="danger"
        onConfirm={runDelete}
        busy={deleteM.isPending}
      />
    </div>
  )
}
